import { Component, Inject, ViewChild, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import * as XLSX from 'xlsx';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { ExternalInvoiceService } from '@app/services/entities/external-invoice.service';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';

export interface ArmstrongRow {
    uuid: string;
    folio: string;
    fechaEmision: string;
    total: number;
    receptorNombre: string;
    proveedorNombre: string;
    folioRemision: string;
    folioOrden: string;
    estatusOriginal: string;
    estatusSistema: string;
    clave: string;
    entidadFederativa: string;
    almacen: string;
    fechaPago: string;
    fechaIngresoCobro: string;
    estatusPortal: string;
    metadata: Record<string, any>;
    _valid: boolean;
    _errors: string[];
    _isDuplicate: boolean;
    _rowIndex: number;
}

const STATUS_MAP: Record<string, string> = {
    '01) PAGADO': 'Pagada',
    '02) INGRESADO A COBRO': 'Contabilidad',
    '03) PENDIENTE INGRESAR A COBRO': 'Facturada',
    '04) EN RUTA': 'Validación de Remisión',
    '05) SIN FACTURA': 'Sin Factura',
    '08) PENDIENTE POR: CONTRATO, FIANZA, CONVENIO MOD.': 'Rechazada',
    '13) REMISION/FACTURA CANCELADA': 'Cancelada',
    '15) PRESCRITO': 'Prescrito',
};

const REQUIRED_COLUMNS = [
    'Folio Fiscal', 'Factura', 'Fecha de emisión', 'Total',
    'Institución', 'Operador', 'Estatus cobranza'
];

@Component({
    selector: 'app-upload-armstrong-dialog',
    standalone: true,
    imports: [CommonModule, MaterialModule, MatPaginatorModule, MatTableModule],
    templateUrl: './upload-armstrong-dialog.component.html',
    styleUrl: './upload-armstrong-dialog.component.scss'
})
export class UploadArmstrongDialogComponent {
    isLoading = false;
    isSaving = false;
    fileName = '';
    step: 'upload' | 'preview' | 'result' = 'upload';

    allRows = signal<ArmstrongRow[]>([]);
    _duplicate = computed(() => this.allRows().filter(r => r._isDuplicate && r._valid));
    validNewRows = computed(() => this.allRows().filter(r => r._valid && !r._isDuplicate));
    errorRows = computed(() => this.allRows().filter(r => !r._valid));

    validDataSource = new MatTableDataSource<ArmstrongRow>();
    duplicateDataSource = new MatTableDataSource<ArmstrongRow>();
    errorDataSource = new MatTableDataSource<ArmstrongRow>();

    validDisplayedColumns = ['_rowIndex', 'uuid', 'folio', 'receptorNombre', 'total', 'estatusSistema', 'estatusOriginal'];
    duplicateDisplayedColumns = ['_rowIndex', 'uuid', 'folio', 'receptorNombre', 'total', 'estatusOriginal', '_duplicateReason'];
    errorDisplayedColumns = ['_rowIndex', 'uuid', 'folio', 'receptorNombre', 'total', 'estatusOriginal', '_errors'];

    @ViewChild('validPaginator') validPaginator!: MatPaginator;
    @ViewChild('duplicatePaginator') duplicatePaginator!: MatPaginator;
    @ViewChild('errorPaginator') errorPaginator!: MatPaginator;

    saveResult = signal<{ success: number; failed: number; duplicates: number; operatorsWithoutRfc: string[] } | null>(null);

    missingColumns: string[] = [];
    totalRowsInFile = 0;
    previewTab: 'valid' | 'duplicate' | 'error' = 'valid';

    constructor(
        public dialogRef: MatDialogRef<UploadArmstrongDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private snackBar: CustomSnackBarService,
        private externalInvoiceService: ExternalInvoiceService,
        private statusTranslator: StatusTranslatorService
    ) {}

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const file = input.files[0];
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            this.snackBar.open('Por favor seleccione un archivo Excel (.xlsx)', 'error');
            return;
        }

        this.fileName = file.name;
        this.isLoading = true;
        this.processFile(file);
    }

    private processFile(file: File): void {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const bstr: string = e.target.result;
                const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary', cellDates: true });
                const sheetName = wb.SheetNames.includes('BASE') ? 'BASE' : wb.SheetNames[0];
                const ws: XLSX.WorkSheet = wb.Sheets[sheetName];
                const rawData = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, defval: '' });

                if (rawData.length < 2) {
                    this.snackBar.open('El archivo está vacío.', 'warning');
                    this.isLoading = false;
                    return;
                }

                const headers = (rawData[0] as string[]).map(h => String(h).trim());
                this.validateAndMap(headers, rawData.slice(1));
            } catch (err) {
                console.error('Error parsing Excel:', err);
                this.snackBar.open('Error al leer el archivo. Verifique el formato.', 'error');
            } finally {
                this.isLoading = false;
            }
        };
        reader.readAsBinaryString(file);
    }

    private validateAndMap(headers: string[], rows: any[][]): void {
        const colMap: Record<string, number> = {};
        headers.forEach((h, i) => { colMap[h] = i; });

        this.missingColumns = REQUIRED_COLUMNS.filter(c => !(c in colMap));

        const getVal = (row: any[], colName: string): any => {
            const idx = colMap[colName];
            return idx !== undefined ? row[idx] : '';
        };

        this.totalRowsInFile = rows.length;
        const mapped = rows
            .filter(row => row.some(cell => cell !== '' && cell != null))
            .map((row, i) => this.mapRow(row, i + 1, getVal));

        this.allRows.set(mapped);
        this.step = 'preview';
        this.setupDataSources();
        this.checkDuplicates();
    }

    private checkDuplicates(): void {
        const rows = this.allRows();
        const uuids = rows.filter(r => r._valid && r.uuid).map(r => r.uuid);
        const folios = rows.filter(r => r._valid && r.folio).map(r => r.folio);

        if (uuids.length === 0 && folios.length === 0) return;

        this.externalInvoiceService.checkExistingKeys(uuids, folios).subscribe({
            next: (existing) => {
                const existingUuidSet = new Set(existing.uuids);
                const existingFolioSet = new Set(existing.folios);

                const updated = rows.map(row => {
                    if (!row._valid) return row;
                    const isDup: boolean = !!(row.uuid && existingUuidSet.has(row.uuid)) || !!(row.folio && existingFolioSet.has(row.folio));
                    if (isDup !== row._isDuplicate) return { ...row, _isDuplicate: isDup };
                    return row;
                });

                this.allRows.set(updated);
                this.setupDataSources();
            },
            error: (err) => {
                console.error('[checkDuplicates] error:', err);
            },
        });
    }

    private mapRow(row: any[], rowIndex: number, getVal: (row: any[], col: string) => any): ArmstrongRow {
        const errors: string[] = [];

        const uuid = String(getVal(row, 'Folio Fiscal') || '').trim();
        const folio = String(getVal(row, 'Factura') || '').trim();
        const fechaEmision = this.parseDate(getVal(row, 'Fecha de emisión'));
        const totalRaw = getVal(row, 'Total');
        const total = parseFloat(String(totalRaw).replace(/[^0-9.-]+/g, '')) || 0;
        const receptorNombre = String(getVal(row, 'Institución') || '').trim();
        const operador = String(getVal(row, 'Operador') || '').trim();
        const folioRemision = String(getVal(row, 'No. Remisión') || '').trim();
        const folioOrden = String(getVal(row, 'No. Orden de Reposición') || '').trim();
        const estatusOriginal = String(getVal(row, 'Estatus cobranza') || '').trim();
        const estatusSistema = this.mapStatus(estatusOriginal);

        const clave = String(getVal(row, 'CLAVE') || '').trim();
        const entidadFederativa = String(getVal(row, 'ENTIDAD FEDERATIVA') || '').trim();
        const almacen = String(getVal(row, 'Nombre almacén') || '').trim();
        const fechaPago = this.parseDate(getVal(row, 'Fecha de pago'));
        const fechaIngresoCobro = this.parseDate(getVal(row, 'Fecha ingreso a cobro'));
        const estatusPortal = String(getVal(row, 'Estatus portal') || '').trim();

        if (!receptorNombre) errors.push('Institución vacía');
        if (!estatusOriginal) errors.push('Estatus cobranza vacío');

        const metadata: Record<string, any> = {};
        const extraCols: [string, string][] = [
            ['NÚMERO DE ALTA', 'numero_alta'],
            ['ESTATUS FACTURA', 'estatus_factura_armstrong'],
            ['Evidencia', 'evidencia'],
            ['CR', 'cr'],
            ['Importe NC', 'importe_nc'],
            ['Nota de crédito', 'nota_credito'],
            ['FECHA INGRESO NC', 'fecha_ingreso_nc'],
            ['ESTATUS IMSS BIENESTAR', 'estatus_imss_bienestar'],
            ['Días Antigüedad Factura', 'dias_antiguedad'],
            ['Rango de antigüedad', 'rango_antiguedad'],
            ['Observación', 'observacion_armstrong'],
        ];

        for (const [colName, metaKey] of extraCols) {
            const val = getVal(row, colName);
            if (val !== '' && val != null) {
                metadata[metaKey] = typeof val === 'object' && val instanceof Date
                    ? val.toISOString().slice(0, 10)
                    : val;
            }
        }

        metadata['estatus_cobranza_original'] = estatusOriginal;
        metadata['source'] = 'HITEC-ARMSTRONG';

        return {
            uuid, folio, fechaEmision, total, receptorNombre,
            proveedorNombre: operador, folioRemision, folioOrden,
            estatusOriginal, estatusSistema,
            clave, entidadFederativa, almacen, fechaPago, fechaIngresoCobro, estatusPortal,
            metadata, _valid: errors.length === 0, _errors: errors, _isDuplicate: false, _rowIndex: rowIndex
        };
    }

    private parseDate(value: any): string {
        if (!value) return '';
        if (value instanceof Date) return value.toISOString().slice(0, 10);
        return String(value);
    }

    private mapStatus(raw: string): string {
        const normalized = raw.trim().toUpperCase();
        for (const [key, val] of Object.entries(STATUS_MAP)) {
            if (normalized === key.toUpperCase()) return val;
        }
        return 'Validación de Remisión';
    }

    private setupDataSources(): void {
        const valid = this.validNewRows();
        const dupes = this._duplicate();
        const errors = this.errorRows();

        this.validDataSource = new MatTableDataSource(valid);
        this.duplicateDataSource = new MatTableDataSource(dupes);
        this.errorDataSource = new MatTableDataSource(errors);

        this.attachActivePaginator();
    }

    onTabChange(tab: 'valid' | 'duplicate' | 'error'): void {
        this.previewTab = tab;
        this.attachActivePaginator();
    }

    private attachActivePaginator(): void {
        setTimeout(() => {
            if (this.previewTab === 'valid' && this.validPaginator) {
                this.validDataSource.paginator = this.validPaginator;
            } else if (this.previewTab === 'duplicate' && this.duplicatePaginator) {
                this.duplicateDataSource.paginator = this.duplicatePaginator;
            } else if (this.previewTab === 'error' && this.errorPaginator) {
                this.errorDataSource.paginator = this.errorPaginator;
            }
        });
    }

    goToStep(step: 'upload' | 'preview' | 'result'): void {
        this.step = step;
    }

    async save(): Promise<void> {
        const valid = this.validNewRows();
        if (valid.length === 0) {
            this.snackBar.open('No hay registros nuevos para guardar.', 'warning');
            return;
        }

        this.isSaving = true;
        let success = 0;
        let failed = 0;
        let duplicatesFromServer = 0;
        const operatorsWithoutRfcSet = new Set<string>();

        const BATCH_SIZE = 250;
        for (let i = 0; i < valid.length; i += BATCH_SIZE) {
            const batch = valid.slice(i, i + BATCH_SIZE);
            const payload = batch.map(row => ({
                id: crypto.randomUUID(),
                external_document_id: crypto.randomUUID(),
                uuid: row.uuid,
                folio: row.folio || null,
                fechaEmision: row.fechaEmision || null,
                total: row.total,
                receptorNombre: row.receptorNombre,
                emisorNombre: row.proveedorNombre,
                proveedorNombre: row.proveedorNombre,
                estatus: row.estatusSistema,
                folioRemision: row.folioRemision || null,
                folioOrden: row.folioOrden || null,
                clave: row.clave || null,
                entidadFederativa: row.entidadFederativa || null,
                almacen: row.almacen || null,
                fechaPago: row.fechaPago || null,
                fechaIngresoCobro: row.fechaIngresoCobro || null,
                estatusPortal: row.estatusPortal || null,
                sourceClientId: `armstrong-${row._rowIndex}`,
                google_metadata: row.metadata,
                statusHistory: [],
                deleted: false,
            }));

            try {
                const res: any = await this.externalInvoiceService.createExternalInvoices(payload).toPromise();
                const info = Array.isArray(res?.data) ? res.data[0] : res?.data;
                success += info?.created_count ?? batch.length;
                duplicatesFromServer += info?.duplicates_count ?? 0;
                for (const name of info?.operators_created_without_rfc ?? []) {
                    operatorsWithoutRfcSet.add(name);
                }
            } catch (err) {
                console.error('Error saving batch:', err);
                failed += batch.length;
            }
        }

        const alreadyMarked = this._duplicate().length;
        this.saveResult.set({
            success,
            failed,
            duplicates: duplicatesFromServer + alreadyMarked,
            operatorsWithoutRfc: [...operatorsWithoutRfcSet],
        });
        this.isSaving = false;
        this.step = 'result';

        if (operatorsWithoutRfcSet.size > 0) {
            this.snackBar.open(
                `${operatorsWithoutRfcSet.size} operador(es) sin RFC fueron agregados al catálogo. Complételos en "Catálogo de Operadores Logísticos".`,
                'warning'
            );
        }
    }

    closeDialog(result: boolean = false): void {
        this.dialogRef.close(result);
    }

    downloadTemplate(): void {
        const headers = [['Folio Fiscal', 'Factura', 'Fecha de emisión', 'Total', 'Institución',
            'Operador', 'No. Remisión', 'No. Orden de Reposición', 'Estatus cobranza',
            'Nombre almacén', 'NÚMERO DE ALTA', 'CLAVE', 'ENTIDAD FEDERATIVA',
            'ESTATUS FACTURA', 'Evidencia', 'CR', 'Estatus portal', 'Importe NC',
            'Nota de crédito', 'FECHA INGRESO NC', 'ESTATUS IMSS BIENESTAR',
            'Fecha de pago', 'Días Antigüedad Factura', 'Rango de antigüedad',
            'Observación']];

        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(headers);

        ws['!cols'] = [
            { wch: 38 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 35 },
            { wch: 12 }, { wch: 16 }, { wch: 22 }, { wch: 45 },
            { wch: 40 }, { wch: 18 }, { wch: 18 }, { wch: 20 },
            { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 12 },
            { wch: 16 }, { wch: 18 }, { wch: 22 },
            { wch: 14 }, { wch: 22 }, { wch: 20 },
            { wch: 30 },
        ];

        const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '3D6091' } } };
        const range = XLSX.utils.decode_range(ws['!ref']!);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const addr = XLSX.utils.encode_cell({ r: 0, c: C });
            if (ws[addr]) ws[addr].s = headerStyle;
        }

        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'BASE');
        XLSX.writeFile(wb, 'Plantilla_Importar_Excel.xlsx');
        this.snackBar.open('Plantilla descargada.', 'success');
    }

    downloadErrors(): void {
        const errors = this.errorRows();
        if (errors.length === 0) return;

        const rows = errors.map(r => [
            r._rowIndex,
            r.uuid || '',
            r.folio || '',
            r.receptorNombre || '',
            r.total,
            r.estatusOriginal || '',
            r._errors.join('; '),
        ]);

        const data = [['#', 'Folio Fiscal', 'Factura', 'Institución', 'Total', 'Estatus original', 'Errores'], ...rows];
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{ wch: 6 }, { wch: 38 }, { wch: 14 }, { wch: 40 }, { wch: 14 }, { wch: 45 }, { wch: 40 }];
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Errores');
        XLSX.writeFile(wb, `errores_importacion_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.snackBar.open(`${errors.length} registros con error descargados.`, 'success');
    }
}
